import * as Icon from 'react-feather';
import { Suspense, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegion } from '../../hooks/useRegion';
import { useSelectedOrCurrentYear } from '../../hooks/useYears';
import { formatJapaneseDateString, formatJapaneseTimeString } from '../../utils/dateUtils';
import { useErrorJian } from '../../hooks/useErrorJian'
import { components } from '../../schema/error-check'
import { ActionPanelButton } from '../../components/ActionPanelButton';
import { LabeledCheckbox } from '../../components/LabeledCheckbox'

type Jian = components["schemas"]["GetErroneousJianListResponse"]["items"][0]

const errorJianFields: (region: string) => [string, string, string][] = (region) => {
  switch (region) {
    case 'Fujisawa':
      return [
        ['awarenessDatetime', '覚知時刻', 'bg-darkblue_50 break-all text-white whitespace-nowrap'],
        ['teamName', '出場隊', ''],
        ['jianNumber', '出場番号', ''],
        ['accidentType', '事故種別', ''],
        ['dispatchLocationAddress', '出場場所ー住所', ''],
        ['isUnsent', '不搬送', ''],
        ['jianSummary', '事案概要', ''],
      ];
    default:
      return [
        ['awarenessDatetime', '覚知時刻', 'bg-darkblue_50 break-all text-white whitespace-nowrap'],
        ['teamName', '出場隊', ''],
        ['kakari', '係', ''],
        ['jianNumber', '事案番号', ''],
        ['accidentType', '事故種別', ''],
        ['dispatchLocationAddress', '出場場所', ''],
        ['isUnsent', '不搬送理由', ''],
        ['jianSummary', '通報概要', ''],
      ];
  }
};

export const ErrorJian = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="container mx-auto overflow-hidden mb-16">
        <ErrorJianContent />
      </div>
    </Suspense>
  );
};

const ErrorJianItemCell = (key: string, value: string | null) => {
  switch (key) {
    case 'awarenessDatetime':
      const date = isNaN(Date.parse(value ?? '')) ? null : new Date(value!);

      return date ? (
        <div>
          <p className="text-center">{formatJapaneseDateString(date)}</p>
          <p className="text-center">{formatJapaneseTimeString(date, 'second')}</p>
        </div>
      ) : (
        <p className="text-center">----</p>
      )
    default:
      return (
        <p className="text-center">{value ?? ''}</p>
      );
  }
};

const ErrorJianItem = (jian: Jian) => {
  const jianId = jian.jianId;
  const navigate = useNavigate();
  const year = useSelectedOrCurrentYear();

  const onClick = useCallback(() => {
    navigate(
      `/${year}/jian/${jianId}/dispatch-info/${jian.dispatchInfoId}?categoryName=出動概要`
    );
  }, [year, jianId, navigate]);

  const fields = errorJianFields(useRegion());

  return (
    <tr className="relative h-32 border-b-[1px] border-b-white cursor-pointer" onClick={onClick}>
      {
        fields.map(([key, label, cn]) => (
          <td key={key} className={`bg-darkblue_10 border-x-[1px] border-x-white ${cn}`}>
            <div className="text-base flex justify-center content-center items-center p-4">
              {ErrorJianItemCell(key, jian[key as keyof Jian]?.toString() ?? null)}
            </div>
          </td>
        ))
      }
    </tr>
  )
};

const PagingBar = () => {
  const { limit, page, isLastPage, incrementPage, decrementPage, changeLimit } = useErrorJian();

  return (
    <div className="h-10 flex flex-row items-center text-xl font-bold gap-4 px-2 bg-darkblue_100 text-white border-r">
      <div>{`${page * limit + 1}件目から${
        isLastPage ? '' : `${(page + 1) * limit}件目`
      }を表示中`}</div>
      <div className="flex-grow">{/* Spacer */}</div>
      <div className="flex items-center gap-2 px-2">
        <span className="font-bold text-sm text-darkblue_70">表示件数</span>
        <select
          className="text-darkblue_90 text-sm rounded border-darkblue_50"
          value={limit}
          onChange={(e) => changeLimit(Number(e.target.value))}
        >
          <option value={10}>10件</option>
          <option value={30}>30件</option>
          <option value={50}>50件</option>
          <option value={100}>100件</option>
          <option value={500}>500件</option>
        </select>
      </div>
      <div className="flex flex-row gap-4">
        {page > 0 && (
          <button
            onClick={() => {
              decrementPage();
            }}
          >
            前の{limit}件
          </button>
        )}
        {!isLastPage && (
          <button
            onClick={() => {
              incrementPage();
            }}
          >
            次の{limit}件
          </button>
        )}
      </div>
    </div>
  )
}

const ErrorJianContent = () => {
  const fields = errorJianFields(useRegion());
  const navigate = useNavigate();
  const { errorJians } = useErrorJian();

  const { condition, search, updateCondition } = useErrorJian();

  return (
    <>
      <div className="top-16 container mx-auto my-4 p-2 rounded bg-darkblue_10 flex items-stretch gap-4">
        <div className="gap-2 px-2 flex items-center">
          <span className="font-bold text-lg text-darkblue_100">月選択</span>
          <select
            className="text-darkblue_90 text-sm rounded border-darkblue_50 mx-2"
            value={condition.monthFrom ?? ''}
            onChange={(e) => updateCondition({ monthFrom: Number(e.target.value) || null })}
          >
            <option value=''> --- </option>
            {
              Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <option key={month} value={month}>{month}月</option>
              ))
            }
          </select>
          ～
          <select
            className="text-darkblue_90 text-sm rounded border-darkblue_50 mx-2"
            value={condition.monthTo ?? ''}
            onChange={(e) => updateCondition({ monthTo: Number(e.target.value) || null })}
          >
            <option value=''> --- </option>
            {
              Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <option key={month} value={month}>{month}月</option>
              ))
            }
          </select>
        </div>
        <div className="gap-2 px-2 flex items-center">
          <LabeledCheckbox
            label="OKエラーを含む"
            checked={condition.warningCount === 1}
            fontsize='lg'
            onChange={(v) => updateCondition({ warningCount: v ? 1 : 0 })}
          />
        </div>
        <div className="flex-1">{/* Spacer */}</div>
        <ActionPanelButton
          type="dark"
          onClick={() => {
            search();
          }}
          label={'検索'}
          icon={<Icon.Search size={24} />}
        />
        <ActionPanelButton
          type="dark"
          onClick={() => navigate(-1)}
          label={'戻る'}
          icon={<Icon.CornerDownLeft size={24} />}
        />
      </div>
      <PagingBar />
      <div className={`border-collapse h-full w-full`}>
        <table className={'w-full'}>
          <thead>
            <tr className="p-0 border-b-[1px] boreder-b-white z-30">
              {
                fields.map(([key, label, _]) => (
                  <th scope="col" key={key} className="p-0 sticky top-0 left-0 z-10 bg-darkblue_100">
                    <div className="z-2 px-4 min-x-full text-xl text-white text-center align-middle whitespace-nowrap">{label}</div>
                  </th>
                ))
              }
            </tr>
          </thead>
          <tbody>
            {errorJians?.items?.map((jian) => (
              <ErrorJianItem key={jian.jianId} {...jian} />
            ))}
          </tbody>
        </table>
      </div>
      <PagingBar />
    </>
  )
};